import React from "react";
import styles from "./ClientDocumentsPage.module.css";
import { Modal } from "react-bootstrap";
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import UploadDocuments from "./components/UploadDocumentsModal/UploadDocuments";
import { ComparisonModal } from "./components/ComparisonModal/ComparisonModal";
import DocumentPreviewModal from "./components/DocumentPreviewModal/DocumentPreviewModal";
import { CreatedDateRenderer } from "pages/K1/K1LandingPage/K1HelperFunctions";
import { confirmImportToCCH, getPBCDocumentsById, getReturnIds, importAllDocumentsToTax } from "./ClientDocumentsPage.service";
import Select from "react-select";
import { ComparisonFullWindowModal } from "pages/K1/CompareK1Page/ComparisonFullWindowModal";
import K1HierarchyInlineView from "./components/InternalK1Hierarchy/K1HierarchyInlineView";
import ConfirmExportToExcelModal from "./components/ConfirmExportToExcelModal/ConfirmExportToExcelModal";
import { StatusCellRenderer } from "./GridStatuses";
import { CygnusGrid } from "@cygnusaiinternal/cygnus-common";
import ActivityMapping from "./components/ActivityMapping/ActivityMapping";
import { GridSearchInput } from "reusable-components/GridSearchInput/GridSearchInput";
import { CustomButton } from "reusable-components/CustomButtonComponent/button";
import type { DestinationReturnIdOption } from "./components/ReturnIdDropdown/ReturnIdDropdown.types";
import { MapActivitiesCell } from "./components/MapActivitiesCell/MapActivitiesCell";
import { SelectReturnIdCell } from "./components/SelectReturnIdCell/SelectReturnIdCell";
import { GridEntityNameCell } from "reusable-components/GridEntityNameCell/GridEntityNameCell";
import type { DocParentMap, ReturnIdOption, ViewMode } from "./ClientDocumentsPage.types";
import {
  getApiSelectedReturnId,
  getDocumentId,
  getSelectedReturnIdValue,
  normalizeSelectedParents,
} from "./ClientDocumentsPage.utils";
import PreviewImportModal from "./components/PreviewImportModal/PreviewImportModal";
import type { PreTransferTieout } from "./components/PreviewImportModal/PreviewImportModal.types";

const getImportResponseValue = (response: any, key: string) => {
  const containers = [response, response?.data, response?.Data];
  const variants = [
    key,
    key.charAt(0).toLowerCase() + key.slice(1),
    key.replace("Tieout", "TieOut"),
    key.toLowerCase(),
  ];

  for (const container of containers) {
    if (!container) continue;
    for (const variant of variants) {
      if (typeof container[variant] !== "undefined") return container[variant];
    }
  }
  return undefined;
};

const normalizePreTransferTieout = (value: any): PreTransferTieout => {
  if (!value) return {};
  let parsedValue = value;

  if (typeof value === "string") {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (typeof parsedValue !== "object" || Array.isArray(parsedValue)) return {};
  return parsedValue as PreTransferTieout;
};

const getRowDestinationReturnId = (row: any) =>
  String(
    row?.InternalK1SelectedReturnId ??
    row?.SelectedReturnId ??
    row?.SelectedReturn ??
    row?.ReturnId ??
    ""
  ).trim();

const buildSourceNamesByReturnId = (rows: any[]) =>
  rows.reduce((acc: Record<string, string>, row: any) => {
    const returnId = getRowDestinationReturnId(row);
    const sourceName = String(row?.SourceName ?? "").trim();
    if (!returnId || !sourceName) return acc;
    if (!acc[returnId]) {
      acc[returnId] = sourceName;
    } else if (!acc[returnId].split(", ").includes(sourceName)) {
      acc[returnId] = `${acc[returnId]}, ${sourceName}`;
    }
    return acc;
  }, {});

const buildSourceTaxIdsByReturnId = (rows: any[]) =>
  rows.reduce((acc: Record<string, string>, row: any) => {
    const returnId = getRowDestinationReturnId(row);
    const sourceTaxId = String(row?.SourceTaxId ?? "").trim();
    if (returnId && sourceTaxId && !acc[returnId]) acc[returnId] = sourceTaxId;
    return acc;
  }, {});

const ClientDocumentsPage: React.FC<any> = ({
  show, onHide, clientmasterid, clientid, clientname, clientoffice, transporter
}) => {
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [documentsData, setDocumentsData] = useState<any[]>([]);
  const [uploadDocumentsModal, setUploadDocumentsModal] = useState(false);
  const [pdfFileUrl, setPdfFileUrl] = useState<string | null>();
  const [documentPreviewModal, setDocumentPreviewModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [comparisonModalShow, setComparisonModalShow] = useState(false);
  const [comparisonData, setComparisonData] = useState({});
  const [fileUploadingFlag, setFileUploadingFlag] = useState(false);
  const [showExcelExportModal, setShowExcelExportModal] = useState(false);
  const [key, setKey] = useState<number | undefined>(undefined);
  const [flowOrchestratorId, setFlowOrchestratorId] = useState<number | null>(null);
  const [returnIdOptions, setReturnIdOptions] = useState<ReturnIdOption[]>([]);
  const [selectedReturnId, setSelectedReturnId] = useState<ReturnIdOption | null>(null);
  const [comparisonWindowModalState, setComparisonWindowModalState] = useState(false);
  const [totalPagesOfSelectedFile, setTotalPages] = useState(0);
  const [documentInternalK1SelectedParentId, setDocumentInternalK1SelectedParentId] = useState<DocParentMap>({});
  const selectableStatuses = useMemo(() => ['Completed', 'Complete', 'Approved', 'Return Selected', 'Imported', 'Verified'], []);
  const gridRef = useRef(null);
  const pendingAutoSelectRef = useRef<Set<number | string>>(new Set());
  const [importingToTax, setImportingToTax] = useState(false);
  const [isOptionValid, setIsOptionValid] = useState(false);
  const [showActivityMappingmodal, setShowActivityMappingmodal] = useState(false);
  const [parentReturnId, setParentReturnId] = useState<any>(null);
  const [parentName, setParentName] = useState<any>(null);
  const [sourceIdentifier, setSourceIdentifier] = useState<any>(null);
  const [documentId, setDocumentId] = useState<any>(null);
  const [internalK1Activities, setInternalK1Activities] = useState<any[]>([]);
  const [fileType, setFileType] = useState<string>("");
  const [flowOrchestratorIdExcelImport, setFlowOrchestratorIdExcelImport] = useState<number | null>(null);
  const [activityMappings, setActivityMappings] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [previewBeforeImport, setPreviewBeforeImport] = useState(true);
  const [graphRefreshKey, setGraphRefreshKey] = useState(0);
  const [showAllHierarchyNodes, setShowAllHierarchyNodes] = useState(false);
  const [showPreviewImportModal, setShowPreviewImportModal] = useState(false);
  const [previewImportLoading, setPreviewImportLoading] = useState(false);
  const [previewImportConfirming, setPreviewImportConfirming] = useState(false);
  const [previewActivityId, setPreviewActivityId] = useState<string | number | null>(null);
  const [preTransferTieout, setPreTransferTieout] = useState<PreTransferTieout>({});
  const [previewImportMessage, setPreviewImportMessage] = useState("");
  const [previewSourceNamesByReturnId, setPreviewSourceNamesByReturnId] = useState<Record<string, string>>({});
  const [previewSourceTaxIdsByReturnId, setPreviewSourceTaxIdsByReturnId] = useState<Record<string, string>>({});

  const getDocumentsByClientId = async () => {
    setRefreshGrid(true);
    try {
      const pbcDocuments: any = await getPBCDocumentsById(clientmasterid, selectedReturnId?.value);
      if (pbcDocuments !== undefined) {
        const selectedParentsByDocumentId = normalizeSelectedParents(pbcDocuments.selectedParents);

        pbcDocuments.data.forEach((doc: any) => {
          const docId = getDocumentId(doc);
          const saved = documentInternalK1SelectedParentId[docId];
          const selectedFromApi = selectedParentsByDocumentId[docId] || getApiSelectedReturnId(doc);

          if (saved) {
            doc.InternalK1SelectedReturnId = saved;
          } else {
            doc.InternalK1SelectedReturnId = selectedFromApi;
          }
        });
        setDocumentsData(pbcDocuments.data);
        setKey(pbcDocuments.data.length);
        setFlowOrchestratorId(pbcDocuments.flowOrchestratorId);
        setFlowOrchestratorIdExcelImport(pbcDocuments.flowOrchestratorIdExcelImport);
        setActivityMappings(pbcDocuments.activityMapping);
      }
    } finally {
      setRefreshGrid(false);
    }
  };

  useEffect(() => { void getAllReturnIds(); }, [clientmasterid]);

  useEffect(() => {
    if (!isOptionValid) return;
    void getDocumentsByClientId();
  }, [selectedReturnId, isOptionValid]);

  const filteredData = useMemo(() => {
    if (!docSearchQuery.trim()) return documentsData;
    const q = docSearchQuery.toLowerCase();
    return documentsData.filter(d =>
      d.SourceName?.toLowerCase().includes(q) ||
      d.SourceTaxId?.toLowerCase().includes(q)
    );
  }, [documentsData, docSearchQuery]);

  const selectedImportRows = useMemo(() => {
    const visibleDocumentIds = new Set(filteredData.map((row: any) => row.DocumentId));
    return selectedRows.filter((row: any) => visibleDocumentIds.has(row.DocumentId));
  }, [filteredData, selectedRows]);

  useEffect(() => {
    if (selectedRows.length === selectedImportRows.length) return;
    setSelectedRows(selectedImportRows);
  }, [selectedImportRows, selectedRows.length]);

  const setPropsForActivityMappingModal = useCallback((returnId: any, taxId: any, docId: any, pName: any) => {
    setParentReturnId(returnId);
    setSourceIdentifier(taxId);
    setDocumentId(docId);
    setParentName(pName);
    setShowActivityMappingmodal(true);
  }, []);

  const setDestinationReturnId = useCallback((rowData: any, option: DestinationReturnIdOption | null) => {
    const selectedDestinationReturnId = option?.value ?? '';
    setDocumentInternalK1SelectedParentId(prev => ({
      ...prev,
      [rowData.DocumentId]: selectedDestinationReturnId,
    }));
    setDocumentsData(prev =>
      prev.map(row =>
        row.DocumentId === rowData.DocumentId
          ? {
              ...row,
              InternalK1SelectedReturnId: selectedDestinationReturnId,
            }
          : row
      )
    );
    if (option) {
      pendingAutoSelectRef.current.add(rowData.DocumentId);
    } else {
      selectedRows.forEach((row: any) => {
        if (row.DocumentId !== rowData.DocumentId) {
          pendingAutoSelectRef.current.add(row.DocumentId);
        }
      });
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!pendingAutoSelectRef.current.size) return;
    const pending = new Set(pendingAutoSelectRef.current);
    pendingAutoSelectRef.current.clear();
    setTimeout(() => {
      (gridRef.current as any)?.api?.forEachNode((node: any) => {
        if (pending.has(node.data?.DocumentId)) {
          node.setSelected(true);
        }
      });
    }, 0);
  }, [documentsData]);

  const documentRowSelection = useMemo(() => ({
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
    hideDisabledCheckboxes: false,
    isRowSelectable: (node: any) => {
      const status = node?.data?.Status;
      const hasReturnId = !!node?.data?.InternalK1SelectedReturnId;
      return hasReturnId || (!!status && selectableStatuses.some(
        x => x.toLowerCase() === String(status).toLowerCase()
      ));
    },
  }), [selectableStatuses]);

  const documentColumnDefs = useMemo(() => [
    {
      headerName: 'Source',
      field: 'SourceName',
      flex: 2,
      sortable: false,
      cellRenderer: GridEntityNameCell,
    },
    {
      headerName: 'ID',
      field: 'SourceTaxId',
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Updated On',
      field: 'LastUpdatedOn',
      flex: 1.5,
      sortable: false,
      cellRenderer: (params: any) => CreatedDateRenderer(params.data?.LastUpdatedOn),
    },
    {
      headerName: 'Status',
      field: 'TaxStatus',
      flex: 1,
      sortable: false,
      cellRenderer: StatusCellRenderer,
      cellRendererParams: { onPendingUpload: () => setUploadDocumentsModal(true) },
      cellStyle: { overflow: 'visible' },
    },
    {
      headerName: 'Select Return ID',
      field: 'InternalK1SelectedReturnId',
      flex: 2,
      sortable: false,
      cellRenderer: SelectReturnIdCell,
      cellRendererParams: { onSelectReturnId: setDestinationReturnId },
    },
    {
      headerName: 'Actions',
      field: '',
      cellRenderer: MapActivitiesCell,
      cellRendererParams: { onMapActivities: setPropsForActivityMappingModal },
      cellClass: styles.actionsCell,
      pinned: 'right' as const,
      width: 130,
      maxWidth: 130,
      minWidth: 130,
      flex: 0,
      sortable: false,
      suppressMenu: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    },
  ], [setDestinationReturnId, setPropsForActivityMappingModal]);

  const onSelectionChanged = (rows: any[]) => setSelectedRows(rows);

  const getAllReturnIds = async () => {
    setRefreshGrid(true);
    try {
      const returnIds = await getReturnIds(clientmasterid);
      const options = returnIds
        .map((item: any) => ({ label: item.ReturnId, value: item.ReturnId }))
        .sort((a: ReturnIdOption, b: ReturnIdOption) => {
          const vA = parseInt(a.value.split(':').pop()?.substring(1) || '0', 10);
          const vB = parseInt(b.value.split(':').pop()?.substring(1) || '0', 10);
          return vA - vB;
        });
      const selectedReturnIdFromApi = getSelectedReturnIdValue(
        returnIds.find((item: any) =>
          item?.IsSelected ||
          item?.Selected ||
          item?.SelectedReturnId ||
          item?.SelectedReturnID
        ) ?? null
      );
      const defaultReturnIdOption =
        options.find((option: ReturnIdOption) => option.value === selectedReturnIdFromApi) ??
        options[0];

      setReturnIdOptions(options);
      if (defaultReturnIdOption) setSelectedReturnId(defaultReturnIdOption);
      setIsOptionValid(true);
    } catch {
      setRefreshGrid(false);
    }
  };

  const removeCachedPages = () => {
    for (let i = 1; i <= totalPagesOfSelectedFile; i++) localStorage.removeItem(`Page ${i}`);
  };

  const buildImportPayload = useCallback((rows: any[], previewEditData: boolean) => {
    const activities = (internalK1Activities ?? [])
      .map((activity: any) => ({
        ...activity,
        Mappings: activity.Mappings.map((m: any) =>
          m.ChildActivityNumber?.includes('_temp') ? { ...m, ChildActivityNumber: '' } : m
        ),
      }))
      .filter((activity: any) => activity.Mappings.length > 0);

    const payload = {
      ClientMasterId: clientmasterid,
      SelectedReturn: selectedReturnId?.value,
      SelectedRows: rows.map((item: any) => ({
        DocumentId: item.DocumentId,
        SourceName: item.SourceName,
        DocumentType: item.DocumentType,
        InternalK1SelectedReturnId: item.DocumentType === 'Internal Schedule K-1' ? item.InternalK1SelectedReturnId : '',
      })),
      InternalK1Activities: activities,
      PreviewEditData: previewEditData,
    };

    return payload;
  }, [clientmasterid, selectedReturnId, internalK1Activities]);

  const closePreviewImportModal = useCallback(() => {
    setShowPreviewImportModal(false);
    setPreviewImportLoading(false);
    setPreviewImportConfirming(false);
    setPreviewActivityId(null);
    setPreTransferTieout({});
    setPreviewImportMessage("");
    setPreviewSourceNamesByReturnId({});
    setPreviewSourceTaxIdsByReturnId({});
  }, []);

  const runDirectImportToTax = useCallback(async (rows: any[]) => {
    setImportingToTax(true);
    try {
      const response = await importAllDocumentsToTax(buildImportPayload(rows, false));
      if (response?.success) void getDocumentsByClientId();
    } finally {
      setImportingToTax(false);
    }
  }, [buildImportPayload]);

  const openPreviewImport = useCallback(async (rows: any[]) => {
    setShowPreviewImportModal(true);
    setPreviewImportLoading(true);
    setPreviewImportConfirming(false);
    setPreviewActivityId(null);
    setPreTransferTieout({});
    setPreviewImportMessage("");
    setPreviewSourceNamesByReturnId(buildSourceNamesByReturnId(rows));
    setPreviewSourceTaxIdsByReturnId(buildSourceTaxIdsByReturnId(rows));
    setImportingToTax(true);

    try {
      const response = await importAllDocumentsToTax(
        buildImportPayload(rows, true),
        { showMessage: false },
      );
      const responseMessage = String(getImportResponseValue(response, "message") ?? "").trim();
      setPreviewImportMessage(responseMessage);
      if (response?.success) {
        setPreviewActivityId(getImportResponseValue(response, "ActivityId") ?? null);
        setPreTransferTieout(normalizePreTransferTieout(getImportResponseValue(response, "PreTransferTieout")));
      }
    } finally {
      setPreviewImportLoading(false);
      setImportingToTax(false);
    }
  }, [buildImportPayload]);

  const handleImportToTax = useCallback(async (rows: any[]) => {
    if (previewBeforeImport) {
      await openPreviewImport(rows);
      return;
    }
    await runDirectImportToTax(rows);
  }, [openPreviewImport, previewBeforeImport, runDirectImportToTax]);

  const confirmPreviewImport = useCallback(async () => {
    if (previewActivityId === null || previewActivityId === "") return;

    setPreviewImportConfirming(true);
    try {
      const response = await confirmImportToCCH({
        ActivityId: previewActivityId,
        PreTransferTieout: preTransferTieout,
      });
      if (response?.success) {
        closePreviewImportModal();
        void getDocumentsByClientId();
      }
    } finally {
      setPreviewImportConfirming(false);
    }
  }, [closePreviewImportModal, preTransferTieout, previewActivityId]);

  const importToTax = () => void handleImportToTax(selectedImportRows);

  const importFromGraph = useCallback((rows: any[]) => {
    setSelectedRows(rows);
    void handleImportToTax(rows);
  }, [handleImportToTax]);

  const formatHeaderReturnIdOption = (
    option: ReturnIdOption,
    meta: { context: 'menu' | 'value' },
  ) => meta.context === 'value' ? `Return ID - ${option.label}` : option.label;

  const returnIdSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: 34,
      height: 34,
      width: 272,
      border: '1px solid #e5e7eb',
      borderRadius: 6,
      boxShadow: 'none',
      background: '#fff',
      cursor: 'pointer',
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 10px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({ ...base, padding: '0 10px 0 4px', color: '#111827' }),
    singleValue: (base: any) => ({ ...base, color: '#111827', fontWeight: 500, fontSize: 12, margin: 0 }),
    placeholder: (base: any) => ({ ...base, fontSize: 12, color: '#111827', margin: 0 }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      backgroundColor: state.isFocused ? '#f8fafc' : 'white',
      color: '#111827',
    }),
    menu: (base: any) => ({ ...base, zIndex: 9999, borderRadius: 4, boxShadow: '0 10px 22px rgba(15,23,42,0.14)', marginTop: 6 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <>
      {comparisonModalShow && (
        <ComparisonModal
          show={comparisonModalShow}
          comparisondata={comparisonData}
          onHide={() => setComparisonModalShow(false)}
          refresh={() => getDocumentsByClientId()}
        />
      )}
      {uploadDocumentsModal && (
        <UploadDocuments
          show={uploadDocumentsModal}
          onHide={() => setUploadDocumentsModal(false)}
          clientmasterid={clientmasterid}
          refresh={() => getDocumentsByClientId()}
          setfileuploading={(flag: any) => setFileUploadingFlag(flag)}
          fileUploading={fileUploadingFlag}
          filetype={fileType}
          floworchestratoridexcelimport={flowOrchestratorIdExcelImport}
        />
      )}
      {documentPreviewModal && (
        <DocumentPreviewModal
          show={documentPreviewModal}
          pdfFileUrl={pdfFileUrl}
          onHide={() => setDocumentPreviewModal(false)}
        />
      )}
      {comparisonWindowModalState && (
        <ComparisonFullWindowModal
          show={comparisonWindowModalState}
          onHide={() => { setComparisonWindowModalState(false); removeCachedPages(); }}
          comparisondata={comparisonData}
          settotalpages={(pages: any) => setTotalPages(pages)}
          refresh={() => getDocumentsByClientId()}
        />
      )}
      {showActivityMappingmodal && (
        <ActivityMapping
          show={showActivityMappingmodal}
          onHide={() => setShowActivityMappingmodal(false)}
          returnid={selectedReturnId?.value ?? null}
          parentreturnid={parentReturnId}
          parentName={parentName}
          clientname={clientname}
          clientmasterid={clientmasterid}
          documentid={documentId}
          sourceidentifier={sourceIdentifier}
          setinternalk1activities={setInternalK1Activities}
          internalk1activities={internalK1Activities}
          activitymappings={activityMappings}
          refresh={() => getDocumentsByClientId()}
        />
      )}
      {showExcelExportModal && (
        <ConfirmExportToExcelModal
          show={showExcelExportModal}
          selectedrows={selectedRows}
          onHide={() => setShowExcelExportModal(false)}
          refresh={() => getDocumentsByClientId()}
          importtotaxflag={() => {}}
          returnid={selectedReturnId}
          clientmasterid={clientmasterid}
          internalk1activities={internalK1Activities}
        />
      )}
      {showPreviewImportModal && (
        <PreviewImportModal
          show={showPreviewImportModal}
          loading={previewImportLoading}
          confirming={previewImportConfirming}
          activityId={previewActivityId}
          preTransferTieout={preTransferTieout}
          previewMessage={previewImportMessage}
          sourceNamesByReturnId={previewSourceNamesByReturnId}
          sourceTaxIdsByReturnId={previewSourceTaxIdsByReturnId}
          onHide={closePreviewImportModal}
          onChange={setPreTransferTieout}
          onConfirm={confirmPreviewImport}
        />
      )}

      <Modal
        show={show}
        onHide={() => onHide()}
        size="lg"
        centered
        className={styles.documentModal}
      >
        <Modal.Header className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <button className={styles.backButton} onClick={() => onHide()} aria-label="Go back">
              <Icon.ArrowLeft />
            </button>
            <div className={styles.pageIcon}>
              <Icon.Person />
            </div>
            <div>
              <h4 className={styles.pageTitle}>{clientname}</h4>
              <p className={styles.pageSubtitle}>
                Client ID: {clientid}
                {clientoffice ? <>&nbsp;&middot;&nbsp;Office: {clientoffice}</> : null}
              </p>
            </div>
          </div>
          <div className={styles.pageHeaderRight}>
            <div className={styles.returnIdPill}>
              <Select
                onChange={(opt: ReturnIdOption | null) => {
                  setDocumentInternalK1SelectedParentId({});
                  setSelectedReturnId(opt);
                }}
                value={selectedReturnId}
                placeholder="Return ID - Select Return ID"
                options={returnIdOptions}
                styles={returnIdSelectStyles}
                formatOptionLabel={formatHeaderReturnIdOption}
                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                menuPosition="fixed"
              />
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          <div className={styles.contentCard}>
            <div className={styles.toolbarRow}>
              {viewMode === 'table' && (
                <div className={styles.toolbarLeft}>
                  <GridSearchInput
                    value={docSearchQuery}
                    placeholder="Search by Source Name or ID"
                    onChange={setDocSearchQuery}
                    width={260}
                    height={34}
                  />
                </div>
              )}
              {viewMode === 'graph' && (
                <div className={styles.toolbarLeft}>
                  <div className={styles.viewToggle}>
                    <button
                      type="button"
                      className={`${styles.viewToggleBtn} ${!showAllHierarchyNodes ? styles.viewToggleActive : ''}`}
                      onClick={() => setShowAllHierarchyNodes(false)}
                      aria-pressed={!showAllHierarchyNodes}
                    >
                      Only Parents
                    </button>
                    <button
                      type="button"
                      className={`${styles.viewToggleBtn} ${showAllHierarchyNodes ? styles.viewToggleActive : ''}`}
                      onClick={() => setShowAllHierarchyNodes(true)}
                      aria-pressed={showAllHierarchyNodes}
                    >
                      All Collections
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.toolbarRight}>
                {viewMode === 'table' && (
                  <>
                    <span className={styles.selectionBadge}>
                      {selectedImportRows.length} {selectedImportRows.length === 1 ? 'Source' : 'Sources'} Selected
                    </span>
                    <label className={styles.previewLabel}>
                      <input
                        type="checkbox"
                        checked={previewBeforeImport}
                        onChange={(e) => setPreviewBeforeImport(e.target.checked)}
                        className={styles.previewCheckbox}
                      />
                      Preview/edit data before importing
                    </label>
                    <CustomButton
                      variant="default"
                      type="button"
                      onclick={importToTax}
                      content={importingToTax ? 'Importing…' : 'Import to CCH'}
                      progress={importingToTax}
                      disable={selectedImportRows.length === 0 || importingToTax}
                      className={styles.importToCCHBtn}
                    />
                  </>
                )}
                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${viewMode === 'table' ? styles.viewToggleActive : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    <Icon.Table />
                    Table View
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${viewMode === 'graph' ? styles.viewToggleActive : ''}`}
                    onClick={() => setViewMode('graph')}
                  >
                    <Icon.Diagram3 />
                    Graph View
                  </button>
                </div>
                <button
                  type="button"
                  className={styles.refreshButton}
                  aria-label="Refresh"
                  onClick={() =>
                    viewMode === 'graph'
                      ? setGraphRefreshKey((k) => k + 1)
                      : void getDocumentsByClientId()
                  }
                >
                  {refreshGrid ? (
                    <ThreeCircles
                      height="15" width="15" visible={true}
                      ariaLabel="three-circles-rotating"
                      outerCircleColor="var(--theme1_primary)"
                      innerCircleColor="var(--theme1_secondary)"
                      middleCircleColor="var(--theme1_tertiary)"
                    />
                  ) : (
                    <Icon.ArrowClockwise />
                  )}
                </button>
              </div>
            </div>

            <div className={styles.gridShell}>
              {viewMode === 'graph' ? (
                <K1HierarchyInlineView
                  returnid={selectedReturnId?.value ?? null}
                  clientMasterId={clientmasterid}
                  refreshKey={graphRefreshKey}
                  showAllNodes={showAllHierarchyNodes}
                  onShowAllNodesChange={setShowAllHierarchyNodes}
                  documentsData={documentsData}
                  documentsLoading={refreshGrid}
                  onSelectReturnId={setDestinationReturnId}
                  onMapActivities={setPropsForActivityMappingModal}
                  onImportToTax={importFromGraph}
                  importingToTax={importingToTax}
                  previewBeforeImport={previewBeforeImport}
                  onPreviewBeforeImportChange={setPreviewBeforeImport}
                />
              ) : (
              <CygnusGrid
                key={key}
                ref={gridRef}
                columnDefs={documentColumnDefs}
                fetchData={async () => ({ records: filteredData, totalCount: filteredData.length })}
                dataIdentifier="DocumentId"
                rowSelection={documentRowSelection as any}
                onLoadingChange={setRefreshGrid}
                cachedIsLoading={refreshGrid}
                cachedData={filteredData}
                setCachedData={setDocumentsData}
                onSelectionChange={onSelectionChanged}
                rowModelType="clientSide"
                pageSize={30}
                rowHeight={40}
              />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ClientDocumentsPage;
